import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "@/lib/api";
import type { MeResponse } from "@/lib/types";

interface AuthState {
  status: "loading" | "authed" | "anonymous";
  me: MeResponse | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<AuthState["status"]>("loading");

  const refresh = async () => {
    try {
      const fresh = await api.me();
      setMe(fresh);
      setStatus("authed");
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setMe(null);
        setStatus("anonymous");
      } else {
        // Network or 500 — surface as anonymous; ErrorBoundary catches deeper errors.
        setMe(null);
        setStatus("anonymous");
      }
    }
  };

  const logout = async () => {
    await fetch("/auth/logout", { method: "POST", credentials: "same-origin" });
    setMe(null);
    setStatus("anonymous");
    window.location.href = "/login";
  };

  // Initial auth check. Immediately after a dev-login / OAuth 302, the first
  // /api/me can race the just-set session cookie and come back 401 (or a
  // cold-start 5xx). Committing to "anonymous" on that first failure bounces a
  // freshly-logged-in user to /login (QA swarm 2026-07-12: guard showed /login
  // + 401 on first render, an immediate re-nav succeeded 200). So retry ONCE
  // before deciding — we stay in "loading" (the guard shows a spinner, never
  // /login) until the retry resolves. refresh() (post-action) keeps its
  // single-shot behavior since the cookie is already established by then.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const fresh = await api.me();
          if (!cancelled) {
            setMe(fresh);
            setStatus("authed");
          }
          return;
        } catch {
          if (attempt === 0) {
            await new Promise((r) => setTimeout(r, 400));
            if (cancelled) return;
            continue; // one retry to absorb the post-redirect cookie race
          }
          if (!cancelled) {
            setMe(null);
            setStatus("anonymous");
          }
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return <Ctx.Provider value={{ status, me, refresh, logout }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (ctx === undefined) {
    throw new Error("useAuth() must be inside <AuthProvider>");
  }
  return ctx;
}
